import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Users, CheckCircle2, XCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

/**
 * /team/accept?token=xxx
 *
 * Public invitation acceptance flow. Fetches invite metadata, lets the
 * invitee either sign in (existing account) or set a password (new account).
 */
export default function TeamAccept() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const { refresh } = useAuth();

  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) { setError("Missing invitation token."); setLoading(false); return; }
    (async () => {
      try {
        const { data } = await api.get(`/team/invitation/${token}`);
        setInvite(data);
      } catch (e) {
        setError(e?.response?.data?.detail || "This invitation is invalid or expired.");
      } finally { setLoading(false); }
    })();
  }, [token]);

  const accept = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/team/accept", { token, name: name.trim(), password });
      if (data.status === "new_account") {
        toast.success("Welcome — you're in.");
        // The backend already set the HttpOnly session cookie on this response.
        // Refresh AuthContext so it picks up the new session before we route in.
        await refresh();
        nav("/dashboard", { replace: true });
      } else {
        toast.success("Team membership added. Sign in to open your workspace.");
        nav(`/login?email=${encodeURIComponent(invite?.invited_email || "")}`, { replace: true });
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not accept invitation.");
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="dms-card p-8 flex items-center gap-3"><Loader2 className="w-5 h-5 animate-spin text-[#1D4E89]" /> Loading invitation…</div>
      </div>
    );
  }
  if (error && !invite) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] px-5">
        <div data-testid="team-accept-error" className="dms-card p-8 max-w-md w-full text-center">
          <div className="w-12 h-12 mx-auto rounded-full bg-[#FEE2E2] flex items-center justify-center mb-4"><XCircle className="w-5 h-5 text-[#B91C1C]" /></div>
          <h1 className="font-heading font-bold text-xl text-[#0A2342]">Invitation unavailable</h1>
          <p className="mt-2 text-[14px] text-[#475569]">{typeof error === "string" ? error : "This invitation is invalid or expired."}</p>
          <Link to="/" className="dms-btn-ghost mt-5 inline-flex">Back to home</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] px-5 py-10">
      <div data-testid="team-accept-page" className="dms-card p-8 max-w-md w-full">
        <div className="w-12 h-12 rounded-full bg-[#0A2342]/10 flex items-center justify-center mb-4">
          <Users className="w-5 h-5 text-[#0A2342]" />
        </div>
        <div className="dms-label">You&apos;ve been invited</div>
        <h1 className="mt-2 font-heading font-bold text-2xl text-[#0A2342] leading-tight">
          Join {invite.workspace_name} as an executor
        </h1>
        <p className="mt-3 text-[14px] text-[#475569] leading-relaxed">
          <b>{invite.invited_by_name || "The workspace owner"}</b> invited <b>{invite.invited_email}</b> to run marketing execution inside their DMS workspace. You&apos;ll get the daily post prompt, see the 90-day calendar and use every AI tool — the owner keeps billing and account controls.
        </p>
        <form onSubmit={accept} className="mt-6 space-y-3">
          <div>
            <label className="dms-label mb-1 block">Your name</label>
            <input
              data-testid="team-accept-name"
              value={name} onChange={(e) => setName(e.target.value)}
              className="dms-input w-full text-[14.5px]" placeholder="Ada Lovelace"
            />
          </div>
          <div>
            <label className="dms-label mb-1 block">Create a password</label>
            <input
              data-testid="team-accept-password"
              type="password" required minLength={8} value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="dms-input w-full text-[14.5px]" placeholder="Minimum 8 characters"
            />
            <div className="mt-1 text-[11.5px] text-[#94A3B8]">If you already have a DMS account with this email, we&apos;ll link it instead — you&apos;ll be asked to sign in.</div>
          </div>
          {error && <div data-testid="team-accept-form-error" className="text-[13px] text-[#B91C1C]">{typeof error === "string" ? error : ""}</div>}
          <button
            data-testid="team-accept-submit"
            type="submit" disabled={submitting || !password || password.length < 8}
            className="dms-btn-primary w-full"
          >
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Joining…</> : <><CheckCircle2 className="w-4 h-4" /> Accept & join</>}
          </button>
        </form>
      </div>
    </div>
  );
}
